import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'form'
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [unsubscribeEmail, setUnsubscribeEmail] = useState('');

  const handleUnsubscribe = async (token, email) => {
    try {
      setStatus('loading');
      
      // If we don't have an email, try to get it from the URL
      const emailToUse = email || searchParams.get('email');
      const tokenToUse = token || searchParams.get('token');
      
      if (!emailToUse && !tokenToUse) {
        setStatus('form');
        return;
      }

      const response = await fetch('/api/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: tokenToUse,
          email: emailToUse 
        }),
      });

      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If response is not JSON, create a fallback error response
        data = {
          success: false,
          message: `Server error: ${response.status} ${response.statusText}`
        };
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to unsubscribe');
      }

      setUnsubscribeEmail(data.email || emailToUse);
      setStatus('success');
      
      // Update URL to remove sensitive data
      navigate('/unsubscribe?success=true', { replace: true });
      
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setError(error.message || 'Ein unerwarteter Fehler ist aufgetreten.');
      setStatus('error');
      // Don't navigate here, just update the state to show the error
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    handleUnsubscribe(null, email);
  };

  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const emailParam = searchParams.get('email');
    const token = searchParams.get('token');

    if (success === 'true') {
      setStatus('success');
      if (emailParam) {
        setUnsubscribeEmail(decodeURIComponent(emailParam));
      }
    } else if (error) {
      setStatus('error');
      setError(
        error === 'unsubscribe_failed' 
          ? 'Die Abmeldung konnte nicht verarbeitet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
          : 'Ein unerwarteter Fehler ist aufgetreten.'
      );
    } else if (token || emailParam) {
      // Process unsubscribe with token or email from URL
      handleUnsubscribe(token, emailParam);
    } else {
      // Show form if no token or email in URL
      setStatus('form');
    }
  }, [searchParams]);

  const statusConfig = {
    loading: {
      title: 'Bitte warten...',
      message: 'Ihre Abmeldung wird bearbeitet.',
      icon: (
        <svg className="w-8 h-8 text-blue-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )
    },
    success: {
      title: 'Abmeldung erfolgreich',
      message: `Die E-Mail-Adresse ${unsubscribeEmail} wurde erfolgreich von unserem Newsletter abgemeldet.`,
      icon: (
        <svg className="w-8 h-8 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )
    },
    error: {
      title: 'Fehler bei der Abmeldung',
      message: error || 'Beim Verarbeiten Ihrer Abmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.',
      icon: (
        <svg className="w-8 h-8 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    form: {
      title: 'Newsletter abbestellen',
      message: 'Bitte geben Sie Ihre E-Mail-Adresse ein, um sich von unserem Newsletter abzumelden.',
      icon: (
        <svg className="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    }
  };

  const currentStatus = statusConfig[status] || statusConfig.error;

  return (
    <div className="min-h-screen bg-[#070709] text-white flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-[#0a0a0b] border border-[#1a1a1a] rounded-xl p-8 shadow-lg">
          {/* Status Icon */}
          <div className="mx-auto w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mb-6">
            {currentStatus.icon}
          </div>
          
          {/* Status Title */}
          <h1 className="text-2xl font-bold text-white text-center mb-4 font-sans">
            {currentStatus.title}
          </h1>
          
          {/* Status Message */}
          <div className="text-center mb-8">
            <p className="text-gray-300">
              {currentStatus.message}
            </p>
            
            {status === 'form' && (
              <form onSubmit={handleSubmit} className="mt-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2 text-left">
                      Ihre E-Mail-Adresse
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ihre@email.de"
                      className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d2d2d] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center"
                    disabled={!email}
                  >
                    Vom Newsletter abmelden
                  </button>
                </div>
              </form>
            )}
          </div>
          
          {/* Back to Home Link */}
          <div className="text-center">
            <a
              href="/"
              className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium text-sm transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Zurück zur Startseite
            </a>
          </div>
        </div>
        
        {/* Support Contact */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 mb-2">Haben Sie Fragen zur Abmeldung?</p>
          <a 
            href="mailto:datenschutz@blcks.at" 
            className="text-sm text-blue-400 hover:underline"
          >
            datenschutz@blcks.at
          </a>
        </div>
      </div>
    </div>
  );
}